import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Heart, Eye, Sparkles, ChevronRight, BookOpen, FolderOpen, Trophy } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

/**
 * Phrase library + favorites stats — extracted from the old /statistics page
 * so it can be inlined under the Leaderboard.
 *
 * Drop-in: <PhraseStatsSection isSpanish={...} />
 */
const PhraseListItem = ({ phrase, index, showRank, isSpanish }) => (
  <Link
    to={`/phrase/${phrase.id}`}
    className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg transition-colors group"
  >
    {showRank && (
      <span className="text-sm font-bold text-muted-foreground w-6 text-center">{index + 1}</span>
    )}
    <div className="flex-1 min-w-0">
      <p className="font-medium text-sm truncate group-hover:text-[#00E5CC] transition-colors">
        {phrase.phrase}
      </p>
      {phrase.view_count !== undefined && phrase.view_count !== null && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Eye className="w-3 h-3" /> {phrase.view_count} {isSpanish ? 'vistas' : 'views'}
        </p>
      )}
      {phrase.favorite_count !== undefined && phrase.favorite_count !== null && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Heart className="w-3 h-3" /> {phrase.favorite_count} {isSpanish ? 'favoritos' : 'favorites'}
        </p>
      )}
    </div>
    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#00E5CC]" />
  </Link>
);

const PhraseStatsSection = () => {
  const navigate = useNavigate();
  const { isSpanish } = useLanguage();
  const { user } = useAuth();
  const [favoritesPhrases, setFavoritesPhrases] = useState([]);
  const [topViewed, setTopViewed] = useState([]);
  const [topFavorited, setTopFavorited] = useState([]);
  const [phraseStats, setPhraseStats] = useState({ total: 0, by_category: {} });
  const [loading, setLoading] = useState(true);

  // Memoised so we don't re-sort & re-slice the by_category object on
  // every render. Recomputes only when phraseStats actually changes.
  const topCategories = useMemo(
    () => Object.entries(phraseStats.by_category)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8),
    [phraseStats.by_category],
  );

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const { data: countData } = await apiFetch('/api/phrases/stats/count');
        if (countData) setPhraseStats(countData);

        if (user) {
          const { data: favData } = await apiFetch('/api/phrases/favorites');
          if (favData) setFavoritesPhrases(favData.phrases || []);
        }

        const { data: viewedData } = await apiFetch('/api/phrases/top-viewed?limit=100');
        if (viewedData) setTopViewed(viewedData.phrases || []);

        const { data: favoritedData } = await apiFetch('/api/phrases/top-favorited?limit=100');
        if (favoritedData) setTopFavorited(favoritedData.phrases || []);
      } catch (err) {
        console.error('Failed to fetch phrase stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="phrase-stats-section">
      {/* Phrase Library Stats */}
      <Card className="bg-gradient-to-r from-[#00E5CC]/10 to-[#8B5CF6]/10 border-[#00E5CC]/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#00E5CC]" />
            {isSpanish ? 'Biblioteca de Frases' : 'Phrase Library'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-background/50 rounded-lg">
              <p className="text-3xl font-bold text-[#00E5CC]">{phraseStats.total.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">{isSpanish ? 'Total de Frases' : 'Total Phrases'}</p>
            </div>
            <div className="text-center p-4 bg-background/50 rounded-lg">
              <p className="text-3xl font-bold text-[#8B5CF6]">{Object.keys(phraseStats.by_category).length}</p>
              <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                <FolderOpen className="w-3 h-3" />
                {isSpanish ? 'Categorías' : 'Categories'}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-2">
              {isSpanish ? 'Categorías Principales:' : 'Top Categories:'}
            </p>
            <div className="flex flex-wrap gap-1">
              {topCategories.map(([cat, count]) => (
                <Badge key={cat} variant="outline" className="text-xs">{cat}: {count}</Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Challenges */}
      <Card
        className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30 cursor-pointer hover:shadow-lg transition-all"
        onClick={() => navigate('/challenges')}
        data-testid="challenges-card"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              {isSpanish ? 'Desafíos' : 'Challenges'}
            </CardTitle>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            {isSpanish
              ? 'Completa desafíos diarios, semanales y mensuales para ganar XP'
              : 'Complete daily, weekly and monthly challenges to earn XP'}
          </p>
          <Button variant="default" size="sm" className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-medium">
            {isSpanish ? 'Ver Desafíos' : 'View Challenges'}
          </Button>
        </CardContent>
      </Card>

      {/* My Favorites */}
      {user && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#00E5CC]" />
              {isSpanish ? 'Mis Frases Favoritas' : 'My Favorite Phrases'}
              {favoritesPhrases.length > 0 && (
                <Badge variant="secondary" className="ml-auto">{favoritesPhrases.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-muted-foreground py-4">{isSpanish ? 'Cargando...' : 'Loading...'}</p>
            ) : favoritesPhrases.length > 0 ? (
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {favoritesPhrases.slice(0, 10).map((phrase, i) => (
                  <PhraseListItem key={phrase.id} phrase={phrase} index={i} isSpanish={isSpanish} />
                ))}
                {favoritesPhrases.length > 10 && (
                  <p className="text-center text-sm text-muted-foreground pt-2">
                    +{favoritesPhrases.length - 10} {isSpanish ? 'más' : 'more'}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4 px-2 text-sm">
                {isSpanish
                  ? '¡Aún no has marcado favoritos! Toca la estrella en una frase para guardarla aquí.'
                  : "No favorites yet! Tap the star on a phrase to save it here."}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Top 100 Viewed */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-500" />
            {isSpanish ? 'Top 100 Frases Vistas' : 'Top 100 Viewed Phrases'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-4">{isSpanish ? 'Cargando...' : 'Loading...'}</p>
          ) : topViewed.length > 0 ? (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {topViewed.map((phrase, i) => (
                <PhraseListItem key={phrase.id} phrase={phrase} index={i} showRank isSpanish={isSpanish} />
              ))}
            </div>
          ) : (
            <div className="text-center py-4 space-y-2">
              <p className="text-muted-foreground text-sm">
                {isSpanish ? 'Las vistas se registran a medida que exploras frases.' : 'Views are tracked as you explore phrases.'}
              </p>
              <Button
                variant="default" size="sm"
                className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]"
                onClick={() => navigate('/library')}
              >
                {isSpanish ? 'Explorar Frases' : 'Explore Phrases'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top 100 Favorited */}
      <Card className="md:col-span-2">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            {isSpanish ? 'Top 100 Frases Favoritas' : 'Top 100 Favorited Phrases'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-4">{isSpanish ? 'Cargando...' : 'Loading...'}</p>
          ) : topFavorited.length > 0 ? (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {topFavorited.map((phrase, i) => (
                <PhraseListItem key={phrase.id} phrase={phrase} index={i} showRank isSpanish={isSpanish} />
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted-foreground text-sm">
                {isSpanish ? 'Marca frases con la estrella para verlas aquí.' : 'Star your favorite phrases to see them ranked here.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PhraseStatsSection;
